import api from './api'
import { PaginationResponse } from '../types/api'

type ApiSuccessResponse<T> = {
  success?: boolean
  data: T
}

export interface Category {
  id: string
  name: string
  slug: string
  description?: string
  imageUrl?: string
  isActive: boolean
  sortOrder: number
  allowsScrapReturn?: boolean
  metaTitle?: string
  metaDescription?: string
  createdAt: string
  updatedAt: string
  _count?: {
    products?: number
  }
}

export interface CategoryFormValues {
  name: string
  description?: string
  isActive: boolean
  sortOrder: number
  allowsScrapReturn?: boolean
  metaTitle?: string
  metaDescription?: string
}

type ListParams = {
  search?: string
  isActive?: boolean
  page?: number
  limit?: number
}

export type AdminCategoryListResponse = {
  categories: Category[]
  pagination: PaginationResponse
}

const adminCategoryService = {
  async list(params: ListParams = {}): Promise<AdminCategoryListResponse> {
    const { search, isActive, page, limit } = params

    const queryParams: Record<string, unknown> = {}

    if (search) queryParams.search = search
    if (isActive !== undefined) queryParams.isActive = isActive
    if (page) queryParams.page = page
    if (limit) queryParams.limit = limit

    const response = await api.get<ApiSuccessResponse<AdminCategoryListResponse>>(
      '/admin/categories',
      { params: queryParams }
    )

    return response.data.data
  },

  async getById(categoryId: string): Promise<Category> {
    const response = await api.get<ApiSuccessResponse<Category>>(
      `/admin/categories/${categoryId}`
    )

    return response.data.data
  },

  async create(payload: CategoryFormValues, imageFile?: File): Promise<Category> {
    if (imageFile) {
      const formData = new FormData()
      formData.append('name', payload.name)
      if (payload.description) formData.append('description', payload.description)
      formData.append('isActive', String(payload.isActive))
      formData.append('sortOrder', String(payload.sortOrder))
      formData.append('allowsScrapReturn', String(payload.allowsScrapReturn ?? false))
      if (payload.metaTitle) formData.append('metaTitle', payload.metaTitle)
      if (payload.metaDescription) formData.append('metaDescription', payload.metaDescription)
      formData.append('image', imageFile)

      const response = await api.post<ApiSuccessResponse<Category>>(
        '/admin/categories',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return response.data.data
    }

    const response = await api.post<ApiSuccessResponse<Category>>(
      '/admin/categories',
      payload
    )
    return response.data.data
  },

  async update(
    categoryId: string,
    payload: CategoryFormValues,
    imageFile?: File
  ): Promise<Category> {
    if (imageFile) {
      const formData = new FormData()
      formData.append('name', payload.name)
      if (payload.description) formData.append('description', payload.description)
      formData.append('isActive', String(payload.isActive))
      formData.append('sortOrder', String(payload.sortOrder))
      formData.append('allowsScrapReturn', String(payload.allowsScrapReturn ?? false))
      if (payload.metaTitle) formData.append('metaTitle', payload.metaTitle)
      if (payload.metaDescription) formData.append('metaDescription', payload.metaDescription)
      formData.append('image', imageFile)

      const response = await api.put<ApiSuccessResponse<Category>>(
        `/admin/categories/${categoryId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return response.data.data
    }

    const response = await api.put<ApiSuccessResponse<Category>>(
      `/admin/categories/${categoryId}`,
      payload
    )
    return response.data.data
  },

  async updateStatus(
    categoryId: string,
    isActive: boolean
  ): Promise<Category> {
    const response = await api.patch<ApiSuccessResponse<Category>>(
      `/admin/categories/${categoryId}/status`,
      { isActive }
    )

    return response.data.data
  },

  async remove(categoryId: string): Promise<void> {
    await api.delete(`/admin/categories/${categoryId}`)
  }
}

export default adminCategoryService

